import { useEffect, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { ClipLoader } from 'react-spinners';
import { db } from '../../firebase/firebase';
import { useAuth } from '../../context/AuthContext';
import { resolveCaseOwner } from '../../utils/caseOwner';

function deriveName(si) {
    if (!si) return null;
    const first = si['Patient First Name']?.answer;
    const last = si['Patient Last Name']?.answer;
    const legacy = si['Patient Name']?.answer;
    const clean = (s) =>
        typeof s === 'string' && s.trim() && s.trim().toLowerCase() !== 'information not found'
            ? s.trim() : null;
    const parts = [clean(last), clean(first)].filter(Boolean);
    if (parts.length) return parts.join(', ');
    return clean(legacy);
}

function deriveDob(si) {
    const v = si?.['Date of Birth']?.answer;
    return typeof v === 'string' && v.trim() ? v.trim() : null;
}

function deriveMrn(si) {
    const v = si?.['Medical Record Number']?.answer;
    return typeof v === 'string' && v.trim() ? v.trim() : null;
}

export default function CaseSubBar() {
    const location = useLocation();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const caseId = params.caseId;
    const { user } = useAuth();
    const [data, setData] = useState(null);
    // B4: mirrors EditCase; a colleague's case is read under its owner's path.
    const ownerUid = resolveCaseOwner(searchParams, user?.uid);

    const isCaseRoute = location.pathname.startsWith('/case/') && !!caseId;

    useEffect(() => {
        if (!isCaseRoute || !user?.uid || !ownerUid) {
            setData(null);
            return undefined;
        }
        const ref = doc(db, 'users', ownerUid, 'cases', caseId);
        const unsub = onSnapshot(ref, (snap) => {
            setData(snap.exists() ? snap.data() : null);
        });
        return unsub;
    }, [isCaseRoute, user?.uid, ownerUid, caseId]);

    if (!isCaseRoute) return null;

    const name = deriveName(data?.structuredInfo);
    const status = data?.status;
    const dob = deriveDob(data?.structuredInfo);
    const mrn = deriveMrn(data?.structuredInfo);

    return (
        <div className="case-sub-bar">
            <div className="case-sub-left">
                <Link to="/cases" className="case-breadcrumb">Cases</Link>
                <span className="case-breadcrumb-sep">/</span>
                {name && <span className="case-sub-name">{name}</span>}
                {status?.message && (
                    <span className={`case-status ${status.condition || ''}`}>
                        {status.condition === 'working' && <ClipLoader size={12} color="#fbbf24" />}
                        {status.message}
                    </span>
                )}
            </div>
            <div className="case-sub-meta">
                {dob && <span className="case-meta-chip">DOB: {dob}</span>}
                {mrn && <span className="case-meta-chip">MR#: {mrn}</span>}
            </div>
        </div>
    );
}
